import cx from 'clsx';
import { formatDistanceToNow } from 'date-fns';
import React, { useEffect } from 'react';

import { ReactComponent as BookmarkMarkedIcon } from '@assets/svg/news/Bookmark-marked.svg';
import { ReactComponent as BookmarkIcon } from '@assets/svg/news/Bookmark.svg';
import { ReactComponent as ClockIcon } from '@assets/svg/news/Clock.svg';

import Button from '@components/uikit/Button/Button';

import { useIntersectionObserver } from '@hooks/useIntersectionObserver';
import useTranslation from '@hooks/useTranslation';

import type { IWhatsNewItem } from '@pages/WhatsNew/model/whatsNewList';

import { useWhatsNewToggleFavoriteMutation } from '../../services/useWhatsNewToggleFavoriteMutation';
import style from './style.module.scss';

interface IProps {
  item: IWhatsNewItem;
  setViewedNews: (id: number) => void;
}

const NewItem: React.FC<IProps> = ({ item, setViewedNews }) => {
  const { t } = useTranslation();
  const [ref, isInViewport] = useIntersectionObserver();

  const { newsId, isFavorite, isViewed, title, description, shortDescription, releaseDate } = item;

  const { mutate: whatsNewToggleFavoriteMutate } = useWhatsNewToggleFavoriteMutation();

  const handleMarkChange = () => {
    whatsNewToggleFavoriteMutate({ newsId, isFavorite: !isFavorite });
  };

  useEffect(() => {
    if (isInViewport && !isViewed) {
      setViewedNews(newsId);
    }
  }, [isInViewport]);

  return (
    <div ref={ref} className={style.whatsNewItemWrapper}>
      <div className={style.whatsNewItemBody}>
        <h4
          className={cx(style.whatsNewItemTitle, { [style.notViewed]: !isViewed })}
          dangerouslySetInnerHTML={{ __html: (title || '').trim() }}
        />
        <p
          className={style.whatsNewItemDescription}
          dangerouslySetInnerHTML={{
            __html: (shortDescription || '').trim() || (description || '').trim(),
          }}
        />
      </div>
      <div className={style.whatsNewItemFooter}>
        <div className={style.whatsNewItemDate}>
          <ClockIcon className={style.whatsNewItemDateIcon} />
          <span>{formatDistanceToNow(new Date(releaseDate), { addSuffix: true })}</span>
        </div>
        <div className={style.whatsNewItemMarkFavorite}>
          {isFavorite ? (
            <BookmarkMarkedIcon className={style.whatsNewItemMarkFavoriteIcon} onClick={handleMarkChange} />
          ) : (
            <BookmarkIcon className={style.whatsNewItemMarkFavoriteIcon} onClick={handleMarkChange} />
          )}
          <Button
            type="secondary"
            text={isFavorite ? t('Favorite') : t('Add to favorites')}
            onClick={handleMarkChange}
          />
        </div>
      </div>
    </div>
  );
};

export default NewItem;
